import React, { useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../AuthContext';
import { JOB_BY_ID_QUERY, ARCHIVE_JOB_MUTATION, UPDATE_JOB_ADDRESS_MUTATION } from '../graphql/jobs';
import { CREATE_ADDRESS_MUTATION } from '../graphql/customers';
import QuoteDialog from '../components/QuoteDialog';
import AddressPicker, { AddressOption, AddressPickerHandle } from '../components/Address/AddressPicker';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import './QuoteDetail.css';

interface DraftQuote {
  id: number;
  name: string;
  createdAt: string;
  finalizedQuoteId: number | null;
}

interface JobData {
  id: number;
  name: string;
  description: string | null;
  status: string;
  createdAt: string;
  archivedAt: string | null;
  addressId: number | null;
  addressByAddressId: AddressOption | null;
  customerByCustomerId: { id: number; name: string; addressesByCustomerId: { nodes: AddressOption[] } };
  draftQuotesByJobId: { nodes: DraftQuote[] };
}

export default function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressId, setAddressId] = useState<number | ''>('');
  const [savingAddress, setSavingAddress] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const addressPickerRef = useRef<AddressPickerHandle>(null);

  const { data, loading, refetch } = useQuery(JOB_BY_ID_QUERY, {
    variables: { id: parseInt(id!) },
    skip: !id,
  });

  const [archiveJob] = useMutation(ARCHIVE_JOB_MUTATION);
  const [updateJobAddress] = useMutation(UPDATE_JOB_ADDRESS_MUTATION);
  const [createAddress] = useMutation(CREATE_ADDRESS_MUTATION);

  const job: JobData | undefined = data?.jobById;
  const drafts: DraftQuote[] = job?.draftQuotesByJobId?.nodes || [];
  const pendingDrafts = drafts.filter((d) => !d.finalizedQuoteId);
  const finalizedDrafts = drafts.filter((d) => d.finalizedQuoteId);

  const handleArchiveToggle = async () => {
    if (!job) return;
    await archiveJob({ variables: { id: job.id, archivedAt: job.archivedAt ? null : new Date().toISOString() } });
    refetch();
  };

  if (loading) return <div className="quote-detail-page">Loading...</div>;
  if (!job) return <div className="quote-detail-page"><div className="error-message">Job not found</div></div>;

  const sectionCard: React.CSSProperties = {
    background: 'white', borderRadius: 8, padding: 24, marginBottom: 24,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  };

  return (
    <div className="quote-detail-page">
      <button className="back-button btn-icon" onClick={() => navigate('/jobs')}>
        <ArrowBackIcon fontSize="small" /> Back to Jobs
      </button>

      <div className="detail-container">
        {/* Header */}
        <div className="detail-header">
          <div>
            <h1 style={{ margin: 0 }}>{job.name}</h1>
            <div style={{ marginTop: 6, color: '#6b7280', fontSize: 14 }}>
              <a href={`/customers/${job.customerByCustomerId.id}`} style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}>
                {job.customerByCustomerId.name}
              </a>
              {job.description && <span style={{ marginLeft: 16 }}>{job.description}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {user?.role !== 'worker' && (
              <button
                onClick={handleArchiveToggle}
                style={{ padding: '8px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: 'pointer', color: '#374151' }}
              >
                {job.archivedAt ? 'Unarchive' : 'Archive'}
              </button>
            )}
            {user?.role !== 'worker' && (
              <button
                onClick={() => setShowQuoteDialog(true)}
                style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <AddIcon fontSize="small" /> New Quote
              </button>
            )}
          </div>
        </div>

        {/* Draft quotes */}
        <div style={sectionCard}>
          <h2 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 700 }}>
            Drafts ({pendingDrafts.length})
          </h2>
          {pendingDrafts.length === 0 ? (
            <div style={{ color: '#9ca3af', textAlign: 'center', padding: '24px 0', fontSize: 14 }}>
              No draft quotes yet.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '8px 0', textAlign: 'left', color: '#374151', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</th>
                  <th style={{ padding: '8px 0', textAlign: 'left', color: '#374151', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Created</th>
                </tr>
              </thead>
              <tbody>
                {pendingDrafts.map((d) => (
                  <tr key={d.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 0' }}>
                      <a href={`/draft-quotes/${d.id}`} style={{ color: '#3b82f6', fontWeight: 600, textDecoration: 'none' }}>{d.name}</a>
                    </td>
                    <td style={{ padding: '10px 0', color: '#6b7280' }}>{new Date(d.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Finalized quotes */}
        {finalizedDrafts.length > 0 && (
          <div style={sectionCard}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 700 }}>
              Finalized Quotes ({finalizedDrafts.length})
            </h2>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '8px 0', textAlign: 'left', color: '#374151', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Quote #</th>
                  <th style={{ padding: '8px 0', textAlign: 'left', color: '#374151', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</th>
                  <th style={{ padding: '8px 0', textAlign: 'left', color: '#374151', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Created</th>
                </tr>
              </thead>
              <tbody>
                {finalizedDrafts.map((d) => (
                  <tr key={d.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 0', color: '#6b7280' }}>#{d.finalizedQuoteId}</td>
                    <td style={{ padding: '10px 0' }}>
                      <a href={`/quotes/${d.finalizedQuoteId}`} style={{ color: '#3b82f6', fontWeight: 600, textDecoration: 'none' }}>{d.name}</a>
                    </td>
                    <td style={{ padding: '10px 0', color: '#6b7280' }}>{new Date(d.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Job info */}
        <div style={sectionCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Details</h2>
            {user?.role !== 'worker' && !editingAddress && (
              <button
                className="btn-icon"
                style={{ padding: '6px 12px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#374151' }}
                onClick={() => { setAddressId(job.addressId ?? ''); setEditingAddress(true); }}
              >
                <EditIcon fontSize="small" /> Edit Address
              </button>
            )}
          </div>
          <div className="info-grid">
            <div className="info-row">
              <label>Customer</label>
              <div>{job.customerByCustomerId.name}</div>
            </div>
            <div className="info-row">
              <label>Address</label>
              {editingAddress ? (
                <div style={{ maxWidth: 400 }}>
                  {addressError && (
                    <div style={{ padding: 8, background: '#fee2e2', color: '#dc2626', borderRadius: 4, marginBottom: 8, fontSize: 13 }}>
                      {addressError}
                    </div>
                  )}
                  <AddressPicker
                    ref={addressPickerRef}
                    label=""
                    addresses={job.customerByCustomerId.addressesByCustomerId.nodes}
                    value={addressId}
                    onChange={setAddressId}
                    disabled={savingAddress}
                    onCreateAddress={async (fields) => {
                      const result = await createAddress({
                        variables: {
                          customerId: job.customerByCustomerId.id,
                          domainId: user!.domain_id,
                          street: fields.street,
                          city: fields.city,
                          state: fields.state,
                          zip: fields.zip,
                          addressType: 'primary',
                        },
                      });
                      return result.data.createAddress.address;
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button
                      className="save-button"
                      disabled={savingAddress}
                      onClick={async () => {
                        setSavingAddress(true);
                        setAddressError(null);
                        try {
                          const resolvedAddressId = await addressPickerRef.current!.commit();
                          await updateJobAddress({ variables: { id: job.id, addressId: resolvedAddressId || null } });
                          setEditingAddress(false);
                          await refetch();
                        } catch (err: any) {
                          setAddressError(err.message || 'Failed to save address');
                        } finally {
                          setSavingAddress(false);
                        }
                      }}
                    >
                      Save
                    </button>
                    <button
                      style={{ padding: '10px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' }}
                      disabled={savingAddress}
                      onClick={() => { setEditingAddress(false); setAddressError(null); }}
                    >
                      <CloseIcon fontSize="small" /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  {job.addressByAddressId
                    ? `${job.addressByAddressId.street}, ${job.addressByAddressId.city}, ${job.addressByAddressId.state} ${job.addressByAddressId.zip}`
                    : '—'}
                </div>
              )}
            </div>
            <div className="info-row">
              <label>Status</label>
              <div style={{ textTransform: 'capitalize' }}>{job.status}</div>
            </div>
            <div className="info-row">
              <label>Created</label>
              <div>{new Date(job.createdAt).toLocaleDateString()}</div>
            </div>
            {job.archivedAt && (
              <div className="info-row">
                <label>Archived</label>
                <div>{new Date(job.archivedAt).toLocaleDateString()}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showQuoteDialog && (
        <QuoteDialog
          initialJobId={job.id}
          onComplete={({ quoteId }) => { navigate(`/draft-quotes/${quoteId}`); }}
          onClose={() => setShowQuoteDialog(false)}
        />
      )}
    </div>
  );
}
